import type { InzetSoort, InzetStatus, Inzetregel, MedewerkerInzet } from './medewerker-types'

export const ONDERGRENS = 95
export const BOVENGRENS = 105

export const SOORT_VOLGORDE: InzetSoort[] = [
  'les',
  'mentoraat',
  'coaching',
  'overig',
]

export const SOORT_LABEL: Record<InzetSoort, string> = {
  les: 'Lessen',
  mentoraat: 'Mentoraat',
  coaching: 'Coaching',
  overig: 'Overige taken',
}

/**
 * Elke taaksoort heeft een eigen tint, zodat de verdeling van iemands uren in een
 * oogopslag te lezen is. Tailwind leest klassenamen letterlijk uit de bron, dus de
 * volledige namen staan hier uitgeschreven en worden niet samengesteld.
 */
export const SOORT_KLEUR: Record<InzetSoort, { vlak: string; tekst: string }> = {
  les: { vlak: 'bg-soort-les', tekst: 'text-soort-les' },
  mentoraat: { vlak: 'bg-soort-mentoraat', tekst: 'text-soort-mentoraat' },
  coaching: { vlak: 'bg-soort-coaching', tekst: 'text-soort-coaching' },
  overig: { vlak: 'bg-soort-overig', tekst: 'text-soort-overig' },
}

export const STATUS_LABEL: Record<InzetStatus, string> = {
  overbezet: 'Overbezet',
  onderbezet: 'Onderbezet',
  in_orde: 'In orde',
  aanstelling_onbekend: 'Aanstelling onbekend',
}

export function bezettingPercentage(totaal: number, beschikbaar: number | null): number | null {
  if (beschikbaar === null || beschikbaar <= 0) return null
  return (totaal / beschikbaar) * 100
}

export function bepaalStatus(totaal: number, beschikbaar: number | null): InzetStatus {
  const percentage = bezettingPercentage(totaal, beschikbaar)
  if (percentage === null) return 'aanstelling_onbekend'
  if (percentage > BOVENGRENS) return 'overbezet'
  if (percentage < ONDERGRENS) return 'onderbezet'
  return 'in_orde'
}

export interface Verdeelvak {
  soort: InzetSoort
  label: string
  uren: number
  aandeel: number
}

/** De vier soorttotalen als aandelen, zonder de soorten waar niets in zit. */
export function verdeling(uren: Record<InzetSoort, number>): Verdeelvak[] {
  const totaal = SOORT_VOLGORDE.reduce((som, soort) => som + uren[soort], 0)

  return SOORT_VOLGORDE.filter((soort) => uren[soort] > 0).map((soort) => ({
    soort,
    label: SOORT_LABEL[soort],
    uren: uren[soort],
    aandeel: totaal > 0 ? uren[soort] / totaal : 0,
  }))
}

export function soorturen(inzet: MedewerkerInzet): Record<InzetSoort, number> {
  return {
    les: inzet.lesUren,
    mentoraat: inzet.mentoraatUren,
    coaching: inzet.coachingUren,
    overig: inzet.overigeUren,
  }
}

export function telSoorturenOp(
  inzetten: MedewerkerInzet[],
): Record<InzetSoort, number> {
  return inzetten.reduce<Record<InzetSoort, number>>(
    (som, inzet) => ({
      les: som.les + inzet.lesUren,
      mentoraat: som.mentoraat + inzet.mentoraatUren,
      coaching: som.coaching + inzet.coachingUren,
      overig: som.overig + inzet.overigeUren,
    }),
    { les: 0, mentoraat: 0, coaching: 0, overig: 0 },
  )
}

export function urenPerSoort(regels: Inzetregel[], soort: InzetSoort): number {
  return regels.filter((regel) => regel.soort === soort).reduce((som, regel) => som + regel.uren, 0)
}

export function groepeerPerSoort(regels: Inzetregel[]): Array<{
  soort: InzetSoort
  label: string
  uren: number
  regels: Inzetregel[]
}> {
  return SOORT_VOLGORDE.map((soort) => {
    const eigen = regels.filter((regel) => regel.soort === soort)
    return {
      soort,
      label: SOORT_LABEL[soort],
      uren: eigen.reduce((som, regel) => som + regel.uren, 0),
      regels: eigen,
    }
  }).filter((groep) => groep.regels.length > 0)
}
